/**
 * Agent Enrichment Service (PAN-440)
 *
 * Background poller that computes enrichment fields for each running agent
 * every ~3 seconds and emits `agent.enrichment_changed` domain events when
 * the enrichment state changes.
 *
 * Enrichment fields: agentPhase, hasPendingQuestion, pendingQuestionCount,
 * resolution, resolutionCount.
 *
 * These fields were dropped in the Effect server migration (PAN-428) and are
 * restored here via the event-driven projection pipeline.
 */

import { Effect } from 'effect'
import { listRunningAgents, type AgentState } from '../../../lib/agents.js'
import { computeAgentEnrichment, getAgentJsonlMtime, type AgentEnrichment } from '../../../lib/agent-enrichment.js'
import { getReviewStatusSync } from '../../../lib/review-status.js'
import { withConcurrencyLimit } from '../../../lib/concurrency.js'
import { getEventStore } from '../event-store.js'
import { saveAgentStateAndEmitEvent } from './agent-projection.js'
import { emitActivityEntrySync, emitActivityTtsSync } from '../../../lib/activity-logger.js'
import type { AgentEnrichmentChangedEvent, AgentCreatedEvent, AgentStatusChangedEvent } from '@overdeck/contracts'
import { toAgentStatus, toRole, toAgentResolution } from '../read-model.js'

// ─── Types ────────────────────────────────────────────────────────────────────

type RunningAgent = AgentState & { tmuxActive: boolean }

interface EnrichmentServiceState {
  timer: ReturnType<typeof setInterval> | null
  lastEnrichment: Map<string, AgentEnrichment>
  /** Last known JSONL file mtime per agent — skip re-scan if unchanged */
  lastMtime: Map<string, number | null>
  /** Agent IDs for which we've already emitted agent.created this server lifetime */
  seenAgentIds: Set<string>
  /** Agent IDs for which we've already emitted a status reconciliation event */
  reconciledAgentIds: Set<string>
}

// ─── Diff helpers ─────────────────────────────────────────────────────────────

function enrichmentChanged(prev: AgentEnrichment | undefined, next: AgentEnrichment): boolean {
  if (!prev) return true
  return (
    prev.role !== next.role ||
    prev.hasPendingQuestion !== next.hasPendingQuestion ||
    prev.pendingQuestionCount !== next.pendingQuestionCount ||
    prev.pendingQuestionPrompt !== next.pendingQuestionPrompt ||
    prev.pendingQuestionReason !== next.pendingQuestionReason ||
    prev.pendingInputCount !== next.pendingInputCount ||
    prev.pendingInputKinds.join(',') !== next.pendingInputKinds.join(',') ||
    prev.pendingAskUserQuestion?.toolUseId !== next.pendingAskUserQuestion?.toolUseId ||
    prev.resolution !== next.resolution ||
    prev.resolutionCount !== next.resolutionCount
  )
}

// PAN-1834 — detect the first moment an agent becomes blocked on input and
// build human-readable activity / TTS messages.
export function isAwaitingInputRisingEdge(
  previous: AgentEnrichment | undefined,
  current: AgentEnrichment,
): boolean {
  const previousCount = previous?.pendingInputCount ?? 0
  return previousCount === 0 && current.pendingInputCount > 0
}

export function buildAwaitingInputActivityMessage(
  agentId: string,
  issueId: string | undefined,
  kinds: readonly string[],
): string {
  const kindList = kinds.length > 0 ? kinds.join(', ') : 'input'
  return issueId
    ? `${agentId} on ${issueId} is waiting for ${kindList}`
    : `${agentId} is waiting for ${kindList}`
}

// ─── Poller ───────────────────────────────────────────────────────────────────

async function pollOnce(state: EnrichmentServiceState): Promise<void> {
  let runningAgents: RunningAgent[]
  try {
    runningAgents = await Effect.runPromise(listRunningAgents())
  } catch {
    return
  }

  const eventStore = getEventStore()

  // Only enrich agents that actually have a live tmux session.
  // Stopped agents have no changing state — their enrichment is static.
  const activeAgents = runningAgents.filter(a => a.tmuxActive)

  await Effect.runPromise(withConcurrencyLimit(
    activeAgents.map((agent) => Effect.promise(async () => {
      const { id: agentId, issueId, startedAt } = agent

      // If this agent hasn't been seen since server start, emit agent.created so the
      // read model adds it to agentsById (handles agents started after last cache save).
      if (!state.seenAgentIds.has(agentId)) {
        state.seenAgentIds.add(agentId)
        try {
          const createdEvent: Omit<AgentCreatedEvent, 'sequence'> = {
            type: 'agent.created',
            timestamp: new Date().toISOString(),
            payload: {
              agentId,
              issueId: issueId ?? agentId,
              agent: {
                id: agentId,
                issueId: issueId ?? agentId,
                workspace: agent.workspace || undefined,
                runtime: undefined,
                model: agent.model || undefined,
                status: toAgentStatus(agent.tmuxActive && agent.status === 'stopped' ? 'running' : agent.status),
                startedAt: agent.startedAt || undefined,
                lastActivity: agent.lastActivity || undefined,
                branch: agent.branch || undefined,
                costSoFar: agent.costSoFar,
                sessionId: agent.sessionId || undefined,
                role: toRole(agent.role) ?? 'work',
                hasLiveTmuxSession: agent.tmuxActive,
                hasPendingQuestion: undefined,
                pendingQuestionCount: undefined,
                pendingQuestionPrompt: undefined,
                pendingQuestionReason: undefined,
                resolution: toAgentResolution((agent as { resolution?: unknown }).resolution),
                resolutionCount: undefined,
              },
            },
          }
          // PAN-1908: write-through projection — agents-row upsert + lifecycle
          // event append in one SQLite transaction.
          saveAgentStateAndEmitEvent(agent, createdEvent)
        } catch {
          // Non-fatal — event store may not be ready at startup
        }
      }

      // Reconcile stale status: if tmux is active but state.json says stopped,
      // emit a status_changed event so the read model corrects to 'running'.
      if (agent.tmuxActive && agent.status === 'stopped' && !state.reconciledAgentIds.has(agentId)) {
        state.reconciledAgentIds.add(agentId)
        try {
          const statusEvent: Omit<AgentStatusChangedEvent, 'sequence'> = {
            type: 'agent.status_changed',
            timestamp: new Date().toISOString(),
            payload: {
              agentId,
              status: 'running',
              previousStatus: 'stopped',
              hasLiveTmuxSession: true,
            },
          }
          // PAN-1908: write-through projection — agents-row upsert + lifecycle
          // event append in one SQLite transaction.
          saveAgentStateAndEmitEvent(agent, statusEvent)
        } catch {
          // Non-fatal
        }
      }

      // Determine if the agent's issue has an active specialist
      let hasActiveSpecialist = false
      if (issueId) {
        const reviewStatus = getReviewStatusSync(issueId)
        hasActiveSpecialist =
          reviewStatus?.reviewStatus === 'reviewing' ||
          reviewStatus?.testStatus === 'testing' ||
          reviewStatus?.mergeStatus === 'merging'
      }

      // Skip JSONL scan if file mtime is unchanged (avoids I/O on static sessions).
      // getAgentJsonlMtime returns an Effect — it MUST be run, not awaited directly
      // (awaiting a non-thenable Effect yields the Effect object, never the value).
      const currentMtime = await Effect.runPromise(getAgentJsonlMtime(agentId))
      const prevMtime = state.lastMtime.get(agentId)
      const previousEnrichment = state.lastEnrichment.get(agentId)
      const jsonlUnchanged = prevMtime !== undefined && currentMtime === prevMtime && previousEnrichment?.hasPendingQuestion !== true
      state.lastMtime.set(agentId, currentMtime)

      let enrichment: AgentEnrichment
      try {
        // If JSONL hasn't changed, only re-check runtime state (resolution)
        // by passing a flag that skips the expensive JSONL scan.
        // computeAgentEnrichment returns an Effect — it MUST be run, not awaited
        // directly (awaiting a non-thenable Effect yields the Effect object, so
        // every enrichment field came back undefined → hasPendingQuestion/
        // pendingAskUserQuestion silently dropped for every agent). PAN-1395.
        enrichment = await Effect.runPromise(computeAgentEnrichment(agentId, startedAt, hasActiveSpecialist, jsonlUnchanged))
      } catch {
        return
      }

      // PAN-1834 — on the rising edge of an agent becoming blocked on input,
      // emit an activity entry + TTS so the operator is notified loudly.
      if (isAwaitingInputRisingEdge(previousEnrichment, enrichment)) {
        const message = buildAwaitingInputActivityMessage(agentId, issueId, enrichment.pendingInputKinds)
        const source = toRole(agent.role) ?? 'work'
        emitActivityEntrySync({ source, level: 'warn', message, issueId })
        emitActivityTtsSync({ utterance: message, priority: 1, issueId, source, eventType: 'awaiting_input' })
      }

      if (!enrichmentChanged(previousEnrichment, enrichment)) {
        return
      }

      state.lastEnrichment.set(agentId, enrichment)

      // Emit event — event store assigns the sequence number
      const event: Omit<AgentEnrichmentChangedEvent, 'sequence'> = {
        type: 'agent.enrichment_changed',
        timestamp: new Date().toISOString(),
        payload: {
          agentId,
          role: toRole(agent.role) ?? 'work',
          hasPendingQuestion: enrichment.hasPendingQuestion,
          pendingQuestionCount: enrichment.pendingQuestionCount,
          pendingQuestionPrompt: enrichment.pendingQuestionPrompt,
          pendingQuestionReason: enrichment.pendingQuestionReason,
          pendingInputCount: enrichment.pendingInputCount,
          pendingInputKinds: enrichment.pendingInputKinds,
          pendingAskUserQuestion: enrichment.pendingAskUserQuestion,
          resolution: enrichment.resolution as AgentEnrichmentChangedEvent['payload']['resolution'],
          resolutionCount: enrichment.resolutionCount,
        },
      }

      try {
        await eventStore.appendAsync(event as never)
      } catch {
        // Non-fatal — event store may not be initialized yet at startup
      }
    })),
    4,
  ))

  // Clean up stale entries for agents that have stopped
  const activeIds = new Set(activeAgents.map(a => a.id))
  for (const id of state.lastEnrichment.keys()) {
    if (!activeIds.has(id)) {
      state.lastEnrichment.delete(id)
      state.lastMtime.delete(id)
    }
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

/** Poll interval: 10 seconds (was 3s). The enrichment data changes slowly —
 *  pending questions, resolution state, phase — none of these need sub-second
 *  latency. 10s eliminates ~70% of poller I/O without any user-visible lag. */
const POLL_INTERVAL_MS = 10_000

const serviceState: EnrichmentServiceState = {
  timer: null,
  lastEnrichment: new Map(),
  lastMtime: new Map(),
  seenAgentIds: new Set(),
  reconciledAgentIds: new Set(),
}

export function startAgentEnrichmentService(): void {
  if (serviceState.timer !== null) return // Already running

  serviceState.timer = setInterval(() => {
    pollOnce(serviceState).catch(() => {
      // Swallow errors — poller must not crash the server
    })
  }, POLL_INTERVAL_MS)
}

export function stopAgentEnrichmentService(): void {
  if (serviceState.timer !== null) {
    clearInterval(serviceState.timer)
    serviceState.timer = null
  }
  serviceState.lastEnrichment.clear()
  serviceState.lastMtime.clear()
}
